import { Injectable, Provider } from '@angular/core';
import { BillingCountry } from '@enums/billing-countries';
import { environment } from '@environments/environment';
import {
  ConfigService,
  CreditCardPayment,
  ExternalPayment,
  GiftCardPayment,
  Payment,
  PaymentTypes,
  providePaymentConfig,
  RechargePayment,
} from '@medology/ngx-payment';
import { AbstractPaymentStoreRequest } from '@models/requests/abstract-payment-store-request.model';
import { AbstractSubscriptionBillingUpdateRequest } from '@models/requests/abstract-subscription-billing-update-request.model';
import { PaymentBraintreeStoreRequest } from '@models/requests/payment-braintree-store-request.model';
import { PaymentCreditCardStoreRequest } from '@models/requests/payment-credit-card-store-request.model';
import { PaymentGiftCardStoreRequest } from '@models/requests/payment-gift-card-store-request.model';
import { SubscriptionBillingBraintreeUpdateRequest } from '@models/requests/subscription-billing-braintree-update-request.model';
import { SubscriptionBillingCreditCardUpdateRequest } from '@models/requests/subscription-billing-credit-card-update-request.model';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  /**
   * Enables Recharge in the ngx-payment config.
   *
   * @param {ConfigService} configService the ngx-payment config service
   * @param {string} displayName the display name for the Recharge payment option
   */
  public enableRecharge(configService: ConfigService, displayName: string): void {
    configService.updatePaymentConfig({
      enabled: [
        PaymentTypes.Recharge,
        ...(configService.paymentConfig().enabled ?? []).filter((type) => type !== PaymentTypes.Recharge),
      ],
      rechargeConfig: { displayName },
    });
  }

  /**
   * Returns the primary payment from the payments array.
   *
   * @param {Payment[]} payments the full list of payments for the order
   *
   * @returns {Payment} the primary payment method
   */
  public getPrimaryPayment(payments: Payment[]): Payment {
    if (payments.length === 1) {
      return payments[0];
    }

    return payments.find((p) => !(p instanceof GiftCardPayment));
  }

  /**
   * Returns the gift card payment from the payments array, if present.
   *
   * @param {NgxPaymentPayment[]} payments the full list of payments for the order
   *
   * @returns {GiftCardPayment | undefined} the gift card payment, or `undefined` if not applicable
   */
  public getGiftCardAsSecondaryPayment(payments: Payment[]): GiftCardPayment | undefined {
    if (payments.length === 1) {
      return undefined;
    }

    return payments.find((p) => p instanceof GiftCardPayment);
  }

  /**
   * Determines whether the primary payment is a credit card issued in an unsupported country.
   *
   * @param {Payment[]} payments the full list of payments for the order
   *
   * @returns {boolean} true if the billing country is not US, Canada, or UK
   */
  public hasUnsupportedCreditCard(payments: Payment[]): boolean {
    const primary = this.getPrimaryPayment(payments);

    return primary instanceof CreditCardPayment && primary.billingCountry === BillingCountry.Other;
  }

  /**
   * Determines whether the primary payment is a Recharge payment.
   *
   * @param {Payment[]} payments the full list of payments for the order
   *
   * @returns {boolean} true if the primary payment is a RechargePayment
   */
  public hasRechargePayment(payments: Payment[]): boolean {
    return this.getPrimaryPayment(payments) instanceof RechargePayment;
  }

  /**
   * Determines whether any of the payments is an external payment method.
   *
   * @param {Payment[]} payments the full list of payments for the order
   *
   * @returns {boolean} true if any payment is an instance of ExternalPayment
   */
  public hasExternalPayment(payments: Payment[]): boolean {
    return payments.some((payment) => payment instanceof ExternalPayment);
  }

  /**
   * Configures the gift card display name and API URL in the ngx-payment config.
   *
   * @param {ConfigService} configService the ngx-payment config service
   */
  public setGiftCardConfig(configService: ConfigService): void {
    configService.updatePaymentConfig({
      giftCardConfig: {
        displayName: `${environment.domain} Gift Card`,
        apiUrl: `${environment.analyteCareApi}/api/v1/gift-card-balance`,
      },
    });
  }

  /**
   * Builds the payment requests in the order they should be charged.
   *
   * @param {Payment[]} payments the full list of payments for the order
   * @param {number} total the total amount to charge in cents
   * @param {string} transactionId the order transaction ID
   * @param {string} patientName the patient's full name
   *
   * @returns {AbstractPaymentStoreRequest[]} the ordered payment requests
   */
  public getPaymentRequests(
    payments: Payment[],
    total: number,
    transactionId: string,
    patientName: string,
  ): AbstractPaymentStoreRequest[] {
    const secondaryPayment = this.getGiftCardAsSecondaryPayment(payments);
    const balance = secondaryPayment ? secondaryPayment.balance * 100 : 0;

    return [
      this.createPaymentRequest(this.getPrimaryPayment(payments), total - balance, transactionId, patientName),
      ...(secondaryPayment ? [this.createPaymentRequest(secondaryPayment, balance, transactionId, patientName)] : []),
    ];
  }

  /**
   * Builds the request to save a payment method on the Recurly subscription an order belongs to.
   *
   * Only the primary payment is used: the billing info of a subscription is a single payment method, so gift cards
   * and other secondary payments have no place in it.
   *
   * @param {Payment[]} payments the full list of payments selected on the payment form
   *
   * @returns {AbstractSubscriptionBillingUpdateRequest} the billing info update request
   */
  public getSubscriptionBillingUpdateRequest(payments: Payment[]): AbstractSubscriptionBillingUpdateRequest {
    const payment = this.getPrimaryPayment(payments);

    return payment instanceof CreditCardPayment
      ? new SubscriptionBillingCreditCardUpdateRequest(payment)
      : new SubscriptionBillingBraintreeUpdateRequest(payment instanceof ExternalPayment ? payment.nonce : '');
  }

  /**
   * Creates a payment request from a Payment object.
   *
   * @param {Payment} payment the payment to create the request from
   * @param {number} amount the amount to charge in cents
   * @param {string} transactionId the order transaction ID
   * @param {string} patientName the patient's full name
   *
   * @returns {AbstractPaymentStoreRequest} the payment request
   */
  private createPaymentRequest(
    payment: Payment,
    amount: number,
    transactionId: string,
    patientName: string,
  ): AbstractPaymentStoreRequest {
    switch (true) {
      case payment instanceof GiftCardPayment:
        return new PaymentGiftCardStoreRequest(payment.code, amount, transactionId);

      case payment instanceof ExternalPayment:
        return new PaymentBraintreeStoreRequest(transactionId, amount, payment.nonce);

      case payment instanceof CreditCardPayment: {
        return new PaymentCreditCardStoreRequest(
          transactionId,
          amount,
          {
            cardNumber: payment.cardNumber,
            cardMonth: payment.expirationMonth,
            cardYear: payment.expirationYear,
            billingZipCode: payment.billingZipCode,
            cvv: payment.cardCvv,
          },
          patientName,
        );
      }

      default:
        return new PaymentBraintreeStoreRequest(transactionId, amount, '');
    }
  }
}

/**
 * Defines the display order for all standard payment methods.
 */
export const SORTED_PAYMENT_METHODS: PaymentTypes[] = [
  PaymentTypes.CreditCard,
  PaymentTypes.ApplePay,
  PaymentTypes.PayPal,
  PaymentTypes.GooglePay,
  PaymentTypes.Venmo,
  PaymentTypes.GiftCard,
];

/**
 * Returns the default ngx-payment provider configuration with the given payment methods enabled.
 *
 * @param {PaymentTypes[]} paymentTypes the payment types to enable, in display order
 *
 * @returns {Provider[]} the ngx-payment provider configuration
 */
export function provideDefaultPaymentConfig(paymentTypes: PaymentTypes[] = SORTED_PAYMENT_METHODS): Provider[] {
  return providePaymentConfig({
    environment: environment.production ? 'production' : 'development',
    braintreeToken: environment.braintreeToken,
    enabled: paymentTypes,
    googlePayConfig: {
      merchantId: environment.googlePay.merchantId,
    },
  });
}
